/** Helpers for Event Schedule step (multi-day + multi-time). */

export function emptyScheduleDay() {
  return {
    active: true,
    date: "", // YYYY-MM-DD
    times: [""], // HH:mm
    recurring: false,
  };
}

export function formatDayLabel(dateStr) {
  if (!dateStr) return "";
  const d = new Date(`${dateStr}T12:00:00`);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export function formatTimeLabel(timeStr) {
  if (!timeStr) return "";
  const [h, m] = String(timeStr).split(":").map(Number);
  if (Number.isNaN(h)) return timeStr;
  const d = new Date();
  d.setHours(h, m || 0, 0, 0);
  return d.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

export function scheduleSummaryLines(days = []) {
  const lines = [];
  for (const day of days || []) {
    if (!day?.active || !day.date) continue;
    for (const t of day.times || []) {
      if (!t) continue;
      lines.push(`${formatDayLabel(day.date)} ${formatTimeLabel(t)}`);
    }
  }
  return lines;
}

export function formatShowSlotLabel(dateStr, timeStr) {
  if (!dateStr || !timeStr) return "";
  const d = new Date(`${dateStr}T12:00:00`);
  if (Number.isNaN(d.getTime())) return `${dateStr} | ${timeStr}`;
  const weekday = d.toLocaleDateString("en-GB", { weekday: "short" });
  return `${weekday}, ${formatDayLabel(dateStr)} | ${formatTimeLabel(timeStr)}`;
}

/** Unique calendar dates from schedule (for date-wise tickets). */
export function listScheduleDates(days = []) {
  const dates = [];
  const seen = new Set();
  for (const day of days || []) {
    if (!day?.active || !day.date) continue;
    if (seen.has(day.date)) continue;
    seen.add(day.date);
    const d = new Date(`${day.date}T12:00:00`);
    const weekday = Number.isNaN(d.getTime())
      ? ""
      : d.toLocaleDateString("en-GB", { weekday: "short" });
    dates.push({
      date: day.date,
      label: formatDayLabel(day.date),
      shortLabel: weekday ? `${weekday}, ${formatDayLabel(day.date)}` : formatDayLabel(day.date),
    });
  }
  return dates;
}

/**
 * Make ticket type unique across dates so inventory matching (by type) stays correct.
 * UI can still show the short name; storage uses "GOLD · 31 Jul 2026".
 */
export function uniqueTicketType(name, date) {
  const base = String(name || "").trim();
  if (!base) return "";
  if (!date) return base;
  const label = formatDayLabel(date);
  if (!label) return base;
  // Already suffixed for this date
  if (base.endsWith(` · ${label}`) || base.endsWith(` (${label})`)) return base;
  return `${base} · ${label}`;
}

/** Display name without date suffix */
export function ticketDisplayName(type = "") {
  return String(type || "")
    .replace(/\s*[·•]\s*\d{1,2}\s+\w{3}\s+\d{4}\s*$/i, "")
    .replace(/\s*\(\d{1,2}\s+\w{3}\s+\d{4}\)\s*$/i, "")
    .trim() || String(type || "");
}

/** Flatten schedule days into show slots for tickets UI. */
export function listShowSlots(days = []) {
  const slots = [];
  for (const day of days || []) {
    if (!day?.active || !day.date) continue;
    for (const t of day.times || []) {
      if (!t) continue;
      slots.push({
        date: day.date,
        time: t,
        label: formatShowSlotLabel(day.date, t),
      });
    }
  }
  return slots;
}

/** Combine YYYY-MM-DD + HH:mm → datetime-local string */
export function combineDateTime(dateStr, timeStr) {
  if (!dateStr || !timeStr) return "";
  return `${dateStr}T${timeStr}`;
}

/**
 * Sync scheduleDays into legacy date / endDate / showtimes fields.
 * durationMinutes used to estimate endTime when only start is provided.
 */
export function syncScheduleToLegacy(days = [], durationMinutes = 120) {
  const slots = [];
  for (const day of days) {
    if (!day?.active || !day.date) continue;
    for (const t of day.times || []) {
      if (!t) continue;
      const start = new Date(`${day.date}T${t}:00`);
      if (Number.isNaN(start.getTime())) continue;
      const end = new Date(start.getTime() + Math.max(Number(durationMinutes) || 120, 30) * 60 * 1000);
      slots.push({ start, end, date: day.date, time: t });
    }
  }

  slots.sort((a, b) => a.start - b.start);

  if (!slots.length) {
    return {
      date: "",
      eventEndDate: "",
      showtimes: [{ startTime: "", endTime: "", availableSeats: "", ticketPrice: "" }],
    };
  }

  const toLocal = (d) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const first = slots[0];
  const last = slots[slots.length - 1];

  return {
    date: toLocal(first.start),
    eventEndDate: toLocal(last.end),
    showtimes: slots.map((s) => ({
      startTime: toLocal(s.start),
      endTime: toLocal(s.end),
      availableSeats: "",
      ticketPrice: "",
    })),
  };
}

export function isScheduleComplete(days = [], minDate) {
  const active = (days || []).filter((d) => d.active);
  if (!active.length) return false;
  return active.some((d) => {
    if (!d.date) return false;
    if (minDate && d.date < minDate) return false;
    return (d.times || []).some((t) => Boolean(t));
  });
}

export function minDateISO(fromDate = new Date()) {
  const d = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate() + 1);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
